import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Box,
  Grid,
  Button,
  Typography,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";

const DoctorBooking = ({ departments, onBookingComplete }) => {
  const [formData, setFormData] = useState({ department: "", doctor: "" });
  const [schedule, setSchedule] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [bookedSlots, setBookedSlots] = useState({});

  const todayKey = new Date().toISOString().slice(0, 10);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setSelectedSlot(null);
    setPaymentAmount("");
    setPaymentMode("");
  };

  const generateSlots = () => {
    const slots = [];
    let start = new Date();
    start.setHours(9, 0, 0, 0);
    const end = new Date();
    end.setHours(19, 0, 0, 0);

    while (start < end) {
      const hours = start.getHours();
      if (hours >= 13 && hours < 15) {
        start.setMinutes(start.getMinutes() + 10);
        continue;
      }
      const slotTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      slots.push({ time: slotTime, booked: false });
      start.setMinutes(start.getMinutes() + 10);
    }
    return slots;
  };

  useEffect(() => {
    if (formData.doctor) {
      const doctorBooked = bookedSlots[todayKey]?.[formData.doctor] || [];
      const slots = generateSlots().map((slot) => ({
        ...slot,
        booked: doctorBooked.includes(slot.time),
      }));
      setSchedule(slots);
    } else {
      setSchedule([]);
    }
  }, [formData.doctor, bookedSlots]);

  const selectSlot = (slot) => {
    if (slot.booked) return;
    setSelectedSlot(slot.time);
  };

  const confirmPayment = () => {
    if (!paymentAmount || !paymentMode) {
      alert("Please enter payment amount and select mode.");
      return;
    }




// Generate Patient ID
function generatePatientId() {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const firstChar = letters[Math.floor(Math.random() * letters.length)];
  const rest = uuidv4()
    .replace(/-/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 7); // 7 chars more
  return `PAT-${firstChar}${rest}`;
}

// Example DB check (pseudo)
async function createUniquePatientId(db) {
  let id;
  let exists = true;

  while (exists) {
    id = generatePatientId();
    const found = await db.query("SELECT 1 FROM patients WHERE patient_id = ?", [id]);
    exists = found.length > 0;
  }

  return id;
}


    const patientId = generatePatientId();
    alert(`Booking confirmed! Patient ID: ${patientId}`);

    setBookedSlots((prev) => {
      const doctorBooked = prev[todayKey]?.[formData.doctor] || [];
      return {
        ...prev,
        [todayKey]: {
          ...prev[todayKey],
          [formData.doctor]: [...doctorBooked, selectedSlot],
        },
      };
    });

    // ✅ Call parent reset
    if (onBookingComplete) {
      onBookingComplete();
    }

    // Reset local state
    setFormData({ department: "", doctor: "" });
    setSelectedSlot(null);
    setPaymentAmount("");
    setPaymentMode("");
    setSchedule([]);
  };

  return (
    <Box mt={2}>
      {/* Department & Doctor */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          select
          fullWidth
          name="department"
          label="🏢 Department"
          value={formData.department}
          onChange={handleChange}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}
        >
          {Object.keys(departments).map((dep) => (
            <MenuItem key={dep} value={dep}>
              {dep}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          name="doctor"
          label="🧑‍⚕️ Doctor"
          value={formData.doctor}
          onChange={handleChange}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}
          disabled={!formData.department}
        >
          {formData.department &&
            departments[formData.department].map((doc) => (
              <MenuItem key={doc} value={doc}>
                {doc}
              </MenuItem>
            ))}
        </TextField>
      </Box>

      {/* Schedule */}
      {formData.doctor && (
        <Box mt={4}>
          <Typography variant="h6">Schedule for Dr. {formData.doctor}</Typography>
          <Grid container spacing={1} mt={1}>
            {schedule.map((slot) => (
              <Grid item xs={2} sm={1} key={slot.time}>
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  sx={{
                    bgcolor:
                      slot.booked || selectedSlot === slot.time
                        ? slot.booked
                          ? "red"
                          : "orange"
                        : "green",
                  }}
                  disabled={slot.booked}
                  onClick={() => selectSlot(slot)}
                >
                  {slot.time}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Payment */}
      {selectedSlot && (
        <Box mt={4}>
          <Typography>Selected Slot: <b>{selectedSlot}</b></Typography>
          <TextField
            type="number"
            label="Payment Amount"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            sx={{ mt: 2 }}
            fullWidth
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Payment Mode</InputLabel>
            <Select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              label="Payment Mode"
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="Card">Card</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" sx={{ mt: 2 }} onClick={confirmPayment}>
            Confirm Payment & Book Slot
          </Button>
        </Box>
      )}
    </Box>
  );
};


export default DoctorBooking;
